/**
 * `skaile connector` — operate on connectors declared in skaile.yaml.
 *
 * Usage:
 *   skaile connector catalog [--project-dir]
 *   skaile connector add <driver> [--id] [--access] [--no-install] [--project-dir]
 *   skaile connector remove <id> [--project-dir]
 *   skaile connector status [id] [--project-dir]
 *   skaile connector list [--project-dir]
 *   skaile connector sync [id] [--all] [--project-dir]
 *   skaile connector ops <id> [--project-dir]
 *   skaile connector read <id> <path> [--project-dir]
 *   skaile connector write <id> <path> <content> [--project-dir]
 *   skaile connector entries <id> [path] [--project-dir]
 *   skaile connector search <id> <query> [--project-dir]
 *   skaile connector delete <id> <path> [--project-dir]
 *   skaile connector run <id> <op> [--key value ...] [--project-dir]
 */

import { existsSync } from "node:fs";
import path from "node:path";
import { createInterface } from "node:readline";
import type { ConnectorFieldSpec } from "@skaile/workspaces/connectors";
import type { CatalogEntry } from "@skaile/workspaces/core";
import { Command } from "commander";
import { logErr } from "../helpers.ts";

/**
 * Commander option coercer for a positive-integer flag. Wraps `parseInt` with
 * an explicit radix (Commander would otherwise call it as `parseInt(value,
 * previous)`, passing the previous value as the radix) and rejects non-positive
 * input. Mirrors `parsePositiveInt` in session-logs.
 */
function parsePositiveIntOption(value: string): number {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error(`--limit must be a positive integer (got ${value})`);
  }
  return n;
}

// ── Shared connector setup ───────────────────────────────────────────────────

/**
 * Scan every factory-assets root for connector catalog entries, de-duplicated by
 * name. Roots are override-first (the `SKAILE_FACTORY_ASSETS_DIR` layer ahead of
 * the bundled tree), so an env-supplied manifest shadows the bundled one.
 */
export async function scanFactoryConnectors(
  scanDirectory: (dir: string, repoName: string) => Promise<CatalogEntry[]>,
  roots: string[],
): Promise<CatalogEntry[]> {
  const byName = new Map<string, CatalogEntry>();
  for (const dir of roots) {
    for (const e of await scanDirectory(dir, "factory-assets")) {
      if (e.kind === "connector" && !byName.has(e.name)) byName.set(e.name, e);
    }
  }
  return [...byName.values()];
}

async function connectConnectors(projectDir: string) {
  const res = await import("@skaile/workspaces/connectors");
  res.registerBuiltinConnectors();

  const declarations = res.loadConnectorDeclarations(projectDir);
  if (declarations.length === 0) {
    logErr("No connectors configured. Add connectors to skaile.yaml");
    process.exit(1);
  }

  const manager = new res.ConnectorManager(projectDir, {
    secrets: res.createCliSecretProviderChain(),
  });
  const report = await manager.connectAll(declarations);
  for (const r of report.results) {
    if (!r.connected) {
      console.error(`Warning: Connector "${r.id}" failed to connect: ${r.error}`);
    }
  }

  return manager;
}

function parseOpArgs(args: string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  let i = 0;
  const positional: string[] = [];

  while (i < args.length) {
    const arg = args[i]!;
    if (arg.startsWith("--")) {
      const key = arg.slice(2);
      const next = args[i + 1];
      if (next && !next.startsWith("--")) {
        try {
          result[key] = JSON.parse(next);
        } catch {
          result[key] = next;
        }
        i += 2;
      } else {
        result[key] = true;
        i += 1;
      }
    } else {
      positional.push(arg);
      i += 1;
    }
  }

  if (positional.length === 1) {
    result._positional = positional[0];
    if (!result.sql && !result.key && !result.message && !result.query) {
      result.sql = positional[0];
      result.key = positional[0];
      result.message = positional[0];
      result.query = positional[0];
    }
  } else if (positional.length > 0) {
    result._positional = positional;
  }

  return result;
}

// ── Interactive prompt helpers ─────────────────────────────────────────────

async function promptText(label: string, defaultVal?: string): Promise<string> {
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  const hint = defaultVal ? ` [${defaultVal}]` : "";
  return new Promise((resolve) => {
    rl.question(`  ${label}${hint}: `, (answer) => {
      rl.close();
      resolve(answer.trim() || defaultVal || "");
    });
  });
}

async function promptSecret(label: string): Promise<string> {
  return new Promise((resolve) => {
    process.stdout.write(`  ${label} (hidden): `);
    const stdin = process.stdin;
    stdin.setRawMode?.(true);
    stdin.resume();
    stdin.setEncoding("utf8");
    let value = "";
    const onData = (char: string) => {
      if (char === "\r" || char === "\n") {
        stdin.setRawMode?.(false);
        stdin.pause();
        stdin.removeListener("data", onData);
        process.stdout.write("\n");
        resolve(value);
      } else if (char === "\u0003") {
        process.exit(1); // Ctrl+C
      } else if (char === "\u007f") {
        value = value.slice(0, -1);
      } else {
        value += char;
      }
    };
    stdin.on("data", onData);
  });
}

async function promptField(field: ConnectorFieldSpec): Promise<string | undefined> {
  if (field.sensitive || field.type === "password" || field.type === "api-key") {
    return promptSecret(field.label);
  }
  return promptText(field.label, field.default);
}

// ── Command builder ──────────────────────────────────────────────────────────

/**
 * Creates the `skaile connector` command group.
 *
 * Operates on connectors declared in `skaile.yaml`. Subcommands: `catalog`,
 * `add`, `remove`, `status`, `list`, `sync`, `ops`, `read`, `write`, `entries`,
 * `search`, `delete`, `run`. All subcommands accept `--project-dir`.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/connector#make-connector-command
 */
export function makeConnectorCommand(): Command {
  const cmd = new Command("connector").description("Operate on connectors in a workspace");

  // skaile connector catalog
  cmd
    .command("catalog")
    .description("List all available connector drivers and their dependencies")
    .action(async () => {
      const { scanDirectory, factoryAssetRoots } = await import("@skaile/workspaces/core");
      const entries = await scanFactoryConnectors(scanDirectory, factoryAssetRoots());

      if (entries.length === 0) {
        console.log("No connector descriptors found.");
        return;
      }

      console.log("\nAvailable connector drivers:\n");
      for (const entry of entries) {
        const npmDeps =
          (entry.metadata?.npm_deps as { required?: string[]; optional?: string[] }) ?? {};
        const allDeps = [
          ...(npmDeps.required ?? []),
          ...(npmDeps.optional ?? []).map((d: string) => `${d} (optional)`),
        ];
        const depsStr = allDeps.length > 0 ? `  deps: ${allDeps.join(", ")}` : "";
        console.log(`  ${entry.name.padEnd(18)}  ${entry.name}`);
        console.log(`  ${"".padEnd(18)}  ${entry.description}`);
        if (depsStr) console.log(`  ${"".padEnd(18)}  ${depsStr}`);
        console.log();
      }
      console.log(`Run: skaile connector add <driver> to add one to your project.\n`);
    });

  // skaile connector add <driver>
  cmd
    .command("add <driver>")
    .option("--id <id>", "Connector instance ID (defaults to driver name)")
    .option("--access <level>", "Access level: read-only | read-write", "read-only")
    .option("--no-install", "Skip npm dependency installation")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .description("Add a connector to skaile.yaml and install its npm deps")
    .action(
      async (
        driver: string,
        opts: { id?: string; access: string; install: boolean; projectDir: string },
      ) => {
        const { installNpmPackages } = await import("@skaile/workspaces/connectors");
        const { scanDirectory, factoryAssetRoots, WorkspaceYamlEditor } = await import(
          "@skaile/workspaces/core"
        );

        const projectDir = path.resolve(opts.projectDir);
        const yamlPath = path.join(projectDir, "skaile.yaml");

        if (!existsSync(yamlPath)) {
          logErr("No skaile.yaml found in current directory. Run 'skaile init' first.");
          process.exit(1);
        }

        const entries = await scanFactoryConnectors(scanDirectory, factoryAssetRoots());
        const entry = entries.find((e) => e.name === driver);
        if (!entry) {
          logErr(
            `Unknown driver: "${driver}". ` +
              `Run 'skaile connector catalog' to see available drivers.`,
          );
          process.exit(1);
        }

        console.log(`\nAdding connector: ${entry.name} (${driver})\n`);

        const id = opts.id ?? (await promptText("Connector ID", driver));
        const access = (opts.access as "read-only" | "read-write") ?? "read-only";

        // Prompt for required fields only
        const fields = (entry.metadata?.fields ?? []) as ConnectorFieldSpec[];
        const fieldValues: Record<string, unknown> = {};
        const requiredFields = fields.filter((f) => f.required);
        if (requiredFields.length > 0) {
          console.log("  Configure required fields:");
          for (const field of requiredFields) {
            const value = await promptField(field);
            if (value) fieldValues[field.key] = value;
          }
        }

        // Install npm deps
        const npmDeps =
          (entry.metadata?.npm_deps as { required?: string[]; optional?: string[] }) ?? {};
        const allDeps = [...(npmDeps.required ?? []), ...(npmDeps.optional ?? [])];
        if (opts.install && allDeps.length > 0) {
          console.log(`\nInstalling npm deps: ${allDeps.join(", ")}...`);
          const installResult = await installNpmPackages(allDeps, projectDir);
          if (!installResult.success) {
            logErr(`Failed to install dependencies:\n${installResult.output}`);
            process.exit(1);
          }
          console.log("  ✓ Dependencies installed");
        } else if (!opts.install && allDeps.length > 0) {
          console.log(
            `  ⚠ Skipping install. Add manually: bun add --optional ${allDeps.join(" ")}`,
          );
        }

        // Write to skaile.yaml
        const editor = WorkspaceYamlEditor.load(yamlPath);
        editor.setConnector({
          id,
          driver,
          access,
          options: Object.keys(fieldValues).length > 0 ? fieldValues : undefined,
        });
        editor.save();

        console.log(`\n✓ Connector '${id}' (${driver}) added to skaile.yaml\n`);
      },
    );

  // skaile connector remove <id>
  cmd
    .command("remove <id>")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .description("Remove a connector from skaile.yaml by its instance ID")
    .action(async (id: string, opts: { projectDir: string }) => {
      const { WorkspaceYamlEditor } = await import("@skaile/workspaces/core");

      const projectDir = path.resolve(opts.projectDir);
      const yamlPath = path.join(projectDir, "skaile.yaml");

      if (!existsSync(yamlPath)) {
        logErr("No skaile.yaml found in current directory.");
        process.exit(1);
      }

      const editor = WorkspaceYamlEditor.load(yamlPath);
      const removed = editor.removeConnector(id);

      if (!removed) {
        logErr(`No connector with id '${id}' found in skaile.yaml.`);
        process.exit(1);
      }

      editor.save();
      console.log(`✓ Connector '${id}' removed from skaile.yaml`);
    });

  // skaile connector status [id]
  cmd
    .command("status [id]")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .description("Connect and report health status for one or all connectors")
    .action(async (id: string | undefined, opts: { projectDir: string }) => {
      const res = await import("@skaile/workspaces/connectors");
      res.registerBuiltinConnectors();

      const projectDir = path.resolve(opts.projectDir);
      const declarations = res.loadConnectorDeclarations(projectDir);

      if (declarations.length === 0) {
        console.log("No connectors configured in skaile.yaml.");
        return;
      }

      const targets = id ? declarations.filter((d) => d.id === id) : declarations;
      if (targets.length === 0) {
        logErr(`No connector with id '${id}' found.`);
        process.exit(1);
      }

      const manager = new res.ConnectorManager(projectDir, {
        secrets: res.createCliSecretProviderChain(),
      });
      const report = await manager.connectAll(targets);

      for (const r of report.results) {
        const symbol = r.connected ? "✓" : "✗";
        const detail = r.error ? `  error: ${r.error}` : "";
        const decl = targets.find((d) => d.id === r.id);
        console.log(`  ${symbol}  ${r.id.padEnd(20)}  driver: ${decl?.driver ?? "?"}${detail}`);
      }

      await manager.disconnectAll();
    });

  // skaile connector list
  cmd
    .command("list")
    .description("List all configured connectors")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .action(async (opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const manager = await connectConnectors(projectDir);
      try {
        console.log(JSON.stringify(manager.listConnectors(), null, 2));
      } finally {
        await manager.disconnectAll();
      }
    });

  // skaile connector sync [id] / --all
  cmd
    .command("sync [id]")
    .description("Re-sync a filesystem-face connector or all of them (git pull, S3 refresh, etc.)")
    .option("--all", "Sync all connectors")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .action(async (id: string | undefined, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const manager = await connectConnectors(projectDir);
      try {
        if (opts.all) {
          const results = await manager.syncAll();
          console.log(JSON.stringify(results, null, 2));
        } else if (id) {
          if (!manager.has(id)) {
            logErr(
              `Unknown connector: "${id}". Available: ${manager
                .listConnectors()
                .map((r) => r.id)
                .join(", ")}`,
            );
            process.exit(1);
          }
          const result = await manager.sync(id);
          console.log(JSON.stringify(result, null, 2));
        } else {
          logErr("Provide a connector ID or use --all");
          process.exit(1);
        }
      } finally {
        await manager.disconnectAll();
      }
    });

  // skaile connector ops <id>
  cmd
    .command("ops <id>")
    .description("List available operations for a connector")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .action(async (id: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const manager = await connectConnectors(projectDir);
      try {
        if (!manager.has(id)) {
          logErr(
            `Unknown connector: "${id}". Available: ${manager
              .listConnectors()
              .map((r) => r.id)
              .join(", ")}`,
          );
          process.exit(1);
        }
        console.log(JSON.stringify(manager.getOperations(id), null, 2));
      } finally {
        await manager.disconnectAll();
      }
    });

  // skaile connector read <id> <path>
  cmd
    .command("read <id> <resource-path>")
    .description("Read content from a connector")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .action(async (id: string, resourcePath: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const manager = await connectConnectors(projectDir);
      try {
        const content = await manager.read(id, resourcePath);
        console.log(content?.data?.toString() ?? "(empty)");
      } finally {
        await manager.disconnectAll();
      }
    });

  // skaile connector write <id> <path> <content>
  cmd
    .command("write <id> <resource-path> <content>")
    .description("Write content to a connector at a path")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .action(async (id: string, resourcePath: string, content: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const manager = await connectConnectors(projectDir);
      try {
        await manager.write(id, resourcePath, { data: content });
        console.log("Written.");
      } finally {
        await manager.disconnectAll();
      }
    });

  // skaile connector entries <id> [path]
  cmd
    .command("entries <id> [resource-path]")
    .description("List entries in a connector (files, keys, rows, etc)")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .option("--recursive", "Include nested entries")
    .option("--limit <n>", "Maximum entries to return", parsePositiveIntOption)
    .action(async (id: string, resourcePath: string | undefined, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const manager = await connectConnectors(projectDir);
      try {
        const entries = await manager.list(id, resourcePath, {
          recursive: opts.recursive,
          limit: opts.limit,
        });
        console.log(JSON.stringify(entries, null, 2));
      } finally {
        await manager.disconnectAll();
      }
    });

  // skaile connector search <id> <query>
  cmd
    .command("search <id> <query>")
    .description("Search within a connector for matching content")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .action(async (id: string, query: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const manager = await connectConnectors(projectDir);
      try {
        const results = await manager.search(id, query);
        console.log(JSON.stringify(results, null, 2));
      } finally {
        await manager.disconnectAll();
      }
    });

  // skaile connector delete <id> <path>
  cmd
    .command("delete <id> <resource-path>")
    .description("Delete an entry from a connector")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .action(async (id: string, resourcePath: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const manager = await connectConnectors(projectDir);
      try {
        const ok = await manager.delete(id, resourcePath);
        console.log(ok ? "Deleted." : "Not found.");
      } finally {
        await manager.disconnectAll();
      }
    });

  // skaile connector run <id> <op> [--key value ...]
  cmd
    .command("run <id> <op> [args...]")
    .description("Run a custom adapter operation (e.g. git log, sqlite query)")
    .option("--project-dir <path>", "Workspace directory", process.cwd())
    .allowUnknownOption()
    .action(async (id: string, op: string, args: string[], opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const manager = await connectConnectors(projectDir);
      try {
        if (!manager.has(id)) {
          logErr(
            `Unknown connector: "${id}". Available: ${manager
              .listConnectors()
              .map((r) => r.id)
              .join(", ")}`,
          );
          process.exit(1);
        }
        // Merge trailing unknown options with positional args
        const rawArgs = [...args, ...(opts["--"] ?? [])];
        const opArgs = parseOpArgs(rawArgs);
        const result = await manager.executeOp(id, op, opArgs);
        console.log(result);
      } finally {
        await manager.disconnectAll();
      }
    });

  cmd.action(() => cmd.help());

  return cmd;
}
